import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { Accordion, Alert, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import {
  AdminGrantSongpopMaestroMutation,
  AdminGrantSongpopMaestroMutationVariables,
  AdminResetSongpopMaestroForTestingMutation,
  AdminResetSongpopMaestroForTestingMutationVariables,
  AdminRevokeSongpopMaestroMutation,
  AdminRevokeSongpopMaestroMutationVariables,
  AdminSongpopMaestroStatusFragmentFragment,
  AdminSongpopMaestroStatusQuery,
  AdminSongpopMaestroStatusQueryVariables,
  GetSongpopMaestroConfigQuery,
  GetSongpopMaestroDropsQuery,
  SongpopMaestroUserState,
} from '../../../../../../__gqltypes__';
import {
  ADMIN_GRANT_SONGPOP_MAESTRO,
  ADMIN_RESET_SONGPOP_MAESTRO_FOR_TESTING,
  ADMIN_REVOKE_SONGPOP_MAESTRO,
  ADMIN_SONGPOP_MAESTRO_STATUS,
} from '../../../graphql';
import { formatReward } from '../../../../liveops/components/formatReward';
import {
  SONGPOP_MAESTRO_CONFIG_QUERY,
  SONGPOP_MAESTRO_DROPS_QUERY,
} from '../../../../liveops/screens/SongpopMaestro/graphql';
import { dropStatusBadge } from '../../../../liveops/screens/SongpopMaestro/utils';
import { MaestroPackEditForm } from './MaestroPackEditForm';

/** Active = the flag the game reads for the perks is on; every other state is not active. */
export const isMaestroPackActive = (state: SongpopMaestroUserState) =>
  state === SongpopMaestroUserState.OWNED || state === SongpopMaestroUserState.FLAG_WITHOUT_ROW;

const formatDate = (date?: string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

/**
 * Songpop Maestro of one player. The status is Active when the perks flag is on, Not active
 * otherwise (no repair; the edit form below shows the pass row itself). Support grant/revoke work on every environment,
 * production included; the reason stored on the pass is "<Granted|Revoked> by admin <admin name>".
 */
const MaestroPackDisplay = ({ userId }: { userId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [supportAction, setSupportAction] = useState<null | 'grant' | 'revoke' | 'reset'>(null);
  const [grantDropId, setGrantDropId] = useState('');
  const [grantNotice, setGrantNotice] = useState<string | null>(null);
  const [latest, setLatest] = useState<AdminSongpopMaestroStatusFragmentFragment | null>(null);

  const {
    data,
    loading,
    error: loadError,
  } = useQuery<AdminSongpopMaestroStatusQuery, AdminSongpopMaestroStatusQueryVariables>(
    ADMIN_SONGPOP_MAESTRO_STATUS,
    { variables: { userId }, fetchPolicy: 'no-cache', skip: !expanded }
  );

  const onStatus = (status?: AdminSongpopMaestroStatusFragmentFragment | null) => status && setLatest(status);
  const [grant, { loading: granting, error: grantError }] = useMutation<
    AdminGrantSongpopMaestroMutation,
    AdminGrantSongpopMaestroMutationVariables
  >(ADMIN_GRANT_SONGPOP_MAESTRO, {
    onCompleted: (result) => {
      onStatus(result.adminGrantSongpopMaestro.status);
      setGrantNotice(
        result.adminGrantSongpopMaestro.instantRewardsGranted
          ? 'Pass granted with the exclusive rewards.'
          : 'The player already owned the pass: nothing was granted again.'
      );
    },
  });
  // The exclusive rewards are global (Songpop Maestro admin, member benefits).
  const { data: configData } = useQuery<GetSongpopMaestroConfigQuery>(SONGPOP_MAESTRO_CONFIG_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !expanded,
  });
  const memberConfig = configData?.liveOps.songpopMaestroConfig;
  // Every sale config, ended ones included: a dispute usually comes after the sale is over.
  const { data: dropsData } = useQuery<GetSongpopMaestroDropsQuery>(SONGPOP_MAESTRO_DROPS_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !expanded,
  });
  const drops = [...(dropsData?.liveOps.songpopMaestroDrops ?? [])].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );
  const [revoke, { loading: revoking, error: revokeError }] = useMutation<
    AdminRevokeSongpopMaestroMutation,
    AdminRevokeSongpopMaestroMutationVariables
  >(ADMIN_REVOKE_SONGPOP_MAESTRO, { onCompleted: (result) => onStatus(result.adminRevokeSongpopMaestro) });

  // TEST only: back to never bought, Google Play purchase consumed, so the same account buys again.
  const [resetForTesting, { loading: resetting, error: resetError }] = useMutation<
    AdminResetSongpopMaestroForTestingMutation,
    AdminResetSongpopMaestroForTestingMutationVariables
  >(ADMIN_RESET_SONGPOP_MAESTRO_FOR_TESTING, {
    onCompleted: (result) => {
      onStatus(result.adminResetSongpopMaestroForTesting.status);
      setGrantNotice(`Reset for testing done. ${result.adminResetSongpopMaestroForTesting.storeMessage}`);
    },
  });

  const status = latest ?? data?.liveOps.adminSongpopMaestroStatus ?? null;
  const busy = granting || revoking || resetting;
  const error = loadError ?? grantError ?? revokeError ?? resetError;
  const ignore = () => {
    // surfaced through `error`
  };

  const isActive = !!status && isMaestroPackActive(status.state);

  // One button for what can be done now (revoke an active pass, grant otherwise); the sale config of a
  // grant (preset to the one on sale, else the latest) is asked in the popup.
  const openSupportAction = (action: 'grant' | 'revoke' | 'reset') => {
    setGrantNotice(null);
    if (action === 'grant' && !grantDropId && drops.length > 0) {
      setGrantDropId((drops.find((drop) => dropStatusBadge(drop).label === 'Running') ?? drops[0]).id);
    }
    setSupportAction(action);
  };

  const runSupportAction = () => {
    const run =
      supportAction === 'grant'
        ? grant({ variables: { input: { userId, dropId: grantDropId } } })
        : supportAction === 'reset'
        ? resetForTesting({ variables: { userId } })
        : revoke({ variables: { input: { userId } } });
    // no reason from the admin tool: the server stores "<Granted|Revoked> by admin <admin name>"
    run.catch(ignore);
    setSupportAction(null);
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Songpop Maestro
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              {loading && <Spinner animation="border" />}
              {error && <Alert variant="danger">{error.message}</Alert>}
              {grantNotice && <Alert variant="info">{grantNotice}</Alert>}
              {status && (
                <>
                  <Alert
                    variant={isActive ? 'success' : 'secondary'}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <strong>{isActive ? 'Active' : 'Not active'}</strong>
                    <span>
                      {status.canResetForTesting && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="mr-2"
                          disabled={busy}
                          title="TEST only: revoke + consume the Google Play purchase, so this account can buy again"
                          onClick={() => openSupportAction('reset')}
                        >
                          {resetting ? <Spinner animation="border" size="sm" /> : 'Reset for testing'}
                        </Button>
                      )}
                      {isActive ? (
                        <Button variant="danger" size="sm" disabled={busy} onClick={() => openSupportAction('revoke')}>
                          {revoking ? <Spinner animation="border" size="sm" /> : 'Revoke pass'}
                        </Button>
                      ) : (
                        <Button variant="success" size="sm" disabled={busy} onClick={() => openSupportAction('grant')}>
                          {granting ? <Spinner animation="border" size="sm" /> : 'Grant pass'}
                        </Button>
                      )}
                    </span>
                  </Alert>
                  <MaestroPackEditForm status={status} drops={drops} onSaved={setLatest} disabled={busy} />
                </>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <Modal show={supportAction !== null} onHide={() => setSupportAction(null)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {supportAction === 'grant'
              ? 'Grant the pass?'
              : supportAction === 'reset'
              ? 'Reset for testing?'
              : 'Revoke the pass?'}
          </Modal.Title>
        </Modal.Header>
        {supportAction === 'reset' && (
          <Modal.Body className="text-muted">
            Back to never bought: pass revoked, Google Play purchase consumed (this account can buy again), daily
            diamonds and Live Show reset. Rewards already given stay.
          </Modal.Body>
        )}
        {supportAction === 'grant' && (
          <Modal.Body>
            <Form.Group>
              <Form.Label>Sale config the player bought through</Form.Label>
              <Form.Control as="select" value={grantDropId} onChange={(e) => setGrantDropId(e.target.value)}>
                {drops.map((drop) => (
                  <option key={drop.id} value={drop.id}>
                    {drop.name} - {drop.skuId} - {dropStatusBadge(drop).label} ({formatDate(drop.startDate)})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {memberConfig && (
              <small className="text-muted">
                Exclusive rewards given now:{' '}
                {memberConfig.instantRewards.length > 0
                  ? memberConfig.instantRewards.map((reward) => formatReward(reward)).join(', ')
                  : 'none'}
              </small>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSupportAction(null)}>
            Cancel
          </Button>
          <Button
            variant={supportAction === 'grant' ? 'success' : supportAction === 'reset' ? 'warning' : 'danger'}
            disabled={supportAction === 'grant' && !grantDropId}
            onClick={runSupportAction}
          >
            {supportAction === 'grant' ? 'Grant' : supportAction === 'reset' ? 'Reset' : 'Revoke'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default MaestroPackDisplay;
